import React from "react";
import styles from './Occasion.module.scss';
import { Box, IconButton, Tooltip } from "@material-ui/core";
import Select from 'react-select';
import { ArrowUpward, Check, Email, SportsEsports, FlashOn } from "@material-ui/icons";
import Booking from "./Booking";
import { isEmpty } from "lodash";
import appConfig from '../../../config/config';
import ACBookingStatusPill from '../../atoms/ACBookingStatusPill';
import ACDeliveryStatusSelect from '../../atoms/ACDeliveryStatusSelect';
import { getCurrencySymbolFromCurrencyShortcode, getDecimalNumber } from '../../common/global';
import {
    componentCost,
    getPayoutTrafficColor,
    hasPayoutRatio,
    occasionCost,
} from '../../common/payout';
import {
    getConfirmationState,
    getGameplayState,
    getLatestNotificationProperties,
    getNotificationStateText,
    NOTIFICATION_STATE_COLORS,
} from 'utils/notificationStatus';
import {
    getBookingEventDate,
    getHqEventReference,
    getHqEventUrl,
    hasTicketFulfilment,
} from 'utils/commonFunction';

// The circular header buttons are the occasion-level send actions. They share the
// per-booking row's colour vocabulary: red = needs actioning, grey = queued,
// green = sent.
const stateBorderColors = {
    none: 'rgb(239, 154, 154)',
    queued: 'rgb(189, 189, 189)',
    sent: 'rgb(165, 214, 167)',
};


const dropdownStyles = {
    menuPortal: (base) => ({
        ...base,
        zIndex: 1500,
    }),
    menu: (provided) => ({
        ...provided,

        backgroundColor: 'rgb(255, 255, 255)',
        color: 'rgb(51, 51, 51)',
        borderRadius: 4,
        outline: 0,

        boxShadow:
            '0px 5px 5px -3px rgba(0, 0, 0, 0.2), ' +
            '0px 8px 10px 1px rgba(0, 0, 0, 0.14), ' +
            '0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
        backgroundImage: 'var(--Paper-overlay)',

        position: 'absolute',

        overflowY: 'auto',
        overflowX: 'hidden',

        minWidth: 16,
        minHeight: 16,
        maxWidth: 'calc(100% - 32px)',
        maxHeight: 'calc(100% - 96px)',

        transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)',

        zIndex: 1300,
        opacity: 1,                 // force
        backdropFilter: 'none',
    }),

    menuList: (provided) => ({
        ...provided,
        padding: 0,
        backgroundColor: '#fff',    // IMPORTANT
        opacity: 1,
    }),
};

export default class Occasion extends React.Component {

    constructor(props) {
        super(props);
        this.bookingReference = React.createRef();
        this.state = {
            booking: {},
        };
    }

    componentDidMount() {
        const { booking } = this.state;
        if (!booking) {
            this.setState({
                booking: booking
            })
        }
    }


    renderBookings(bookings) {
  const { booking } = this.state;
  const notActiveStops = this.props.notActiveItems?.map(i => i?.id) || [];
  const { config } = this.props;

  const nodes = [];

  bookings.forEach((b, index) => {
    const isExpanded = booking && booking.id === b.id;
    const refProp = isExpanded ? this.bookingReference : null;

    // A component gets a cost and no ratio: HQ holds one revenue figure for the whole
    // occasion, so there is nothing to divide a component's cost by.
    const cost = componentCost(b);

    nodes.push(
      <div key={`booking-${b.id}`} className={styles.bookingWrapper}>
        <span className={styles.componentCost}>
          {this.currencySymbol()}{getDecimalNumber(cost, 2)}
        </span>
        <Booking
          config={config}
          referenceProperty={refProp}
          booking={b}
          notActiveItems={notActiveStops}
          users={this.props.users}
          isExpanded={isExpanded}
          isOccasionBooking={true}
          onSelectBookingFunc={this.props.onSelectBookingFunc}
          onClickSetRouteFunc={this.props.onClickSetRouteFunc}
          onClickSetVirtualFunc={this.props.onClickSetVirtualFunc}
          onPreviewConfirmationFunc={this.props.onPreviewConfirmationFunc}
          onPreviewCodeFunc={this.props.onPreviewCodeFunc}
          onUpdateBookingItemPaymentFunc={this.props.onUpdateBookingItemPaymentFunc}
          onUpdateBookingItemActivityFunc={this.props.onUpdateBookingItemActivityFunc}
          onUpdateBookingManagerFunc={this.props.onUpdateBookingManagerFunc}
          onDeleteBookingManagerFunc={this.props.onDeleteBookingManagerFunc}
          onUpdateStopBookingNoteFunc={this.props.onUpdateStopBookingNoteFunc}
          onUpdateBookingItemPriceFunc={this.props.onUpdateBookingItemPriceFunc}
          onClickBookingNote={this.props.onClickBookingNote}
          onClickAddBookingStopFunc={this.props.onClickAddBookingStopFunc}
          onClickAddBookingStopSparkFunc={this.props.onClickAddBookingStopSparkFunc}
          onSelectStopFunc={this.props.onSelectStopFunc}
          googleMapURL={this.props.googleMapURL}
          getProductByIdFunc={this.props.getProductByIdFunc}
          onSwapStopsFunc={this.props.onSwapStopsFunc}
          onPublishBookingFunc={this.props.onPublishBookingFunc}
          onUpdatePersonalisationCheckFunc={this.props.onUpdatePersonalisationCheckFunc}
        />
      </div>
    );

    // connector BETWEEN bookings
    if (index < bookings.length - 1) {
      nodes.push(
        <div
          key={`connector-${b.id}`}
          className={styles.connectorGap}
        >
          <span
            className={styles.connectorIcon}
            onClick={() =>
              this.props.onClickAddBookingStopFunc(b)
            }
          >
            +
          </span>
        </div>
      );
    }
  });

  return nodes;
}

    scrollToRef = (reference) => {
        if (reference && reference.current) {
            window.scrollTo(0, reference.current.offsetTop);
        }
    };

    // Set the package owner for the occasion. The occasion is itself a top-level
    // booking document, so it resolves by its own id via the existing handler and needs
    // no occasionBookingId — that is only for its embedded component bookings.
    onChangeBookingManager = (option) => {
        const { occasion, onUpdateBookingManagerFunc, onDeleteBookingManagerFunc } = this.props;
        if (!occasion?.id) return;

        // A cleared option carries no value; assigning `undefined` would PUT the literal
        // string to `.../users/undefined`. Clearing is its own endpoint.
        if (!option?.value) {
            if (onDeleteBookingManagerFunc) onDeleteBookingManagerFunc(occasion.id);
            return;
        }

        if (onUpdateBookingManagerFunc) {
            onUpdateBookingManagerFunc(occasion.id, option.value);
        }
    };


    // Confirmation and gameplay are occasion-level actions: the occasion is the
    // booking document the notification endpoints resolve, and the single HQ event
    // the delivery status lands on. The embedded component bookings are not
    // addressable by those endpoints, so they carry no send actions of their own.
    onClickSendConfirmation = (event) => {
        event.stopPropagation();
        const { occasion, getProductByIdFunc, onPreviewConfirmationFunc } = this.props;
        if (occasion?.productId && getProductByIdFunc) getProductByIdFunc(occasion.productId);
        if (onPreviewConfirmationFunc) onPreviewConfirmationFunc(occasion);
    };

    onClickSendGameplay = (event) => {
        event.stopPropagation();
        const { occasion, getProductByIdFunc, onPreviewCodeFunc } = this.props;
        if (occasion?.productId && getProductByIdFunc) getProductByIdFunc(occasion.productId);
        if (onPreviewCodeFunc) onPreviewCodeFunc(occasion);
    };

    // Nothing to send until at least one component has a route, a virtual or a ticket
    // attached.
    //
    // A ticket cannot be recognised by its routeId: `upsertOneBookingTicketRoute` writes the
    // generated route inline and explicitly $unsets routeId, because a generated route has no
    // record in the routes library to point at. Its stop type is the only marker. This read
    // used to check routeId alone and claim in a comment that a ticket "arrives here as a
    // routeId" — it never did, so an occasion fulfilled entirely by tickets could never be
    // sent, and therefore never reached AVAILABLE by any route but the test affordance below.
    hasDeliverableComponents() {
        const { occasion } = this.props;
        return (occasion?.bookings || []).some(
            (component) =>
                !isEmpty(component?.routeId)
                || !isEmpty(component?.virtualId)
                || hasTicketFulfilment(component)
                // A self-serve component is delivered by content already on it - a walk with a
                // meditation playlist - so there is nothing to attach and never will be. Without
                // this an occasion made only of them could never be sent, which is the same trap
                // ticketed components were in before `hasTicketFulfilment` was added above.
                || component?.selfServe === true
        );
    }

    // The occasion is a real booking document, so it resolves by its own id on the
    // shared delivery-status endpoint — unlike its embedded components.
    onChangeDeliveryStatus = (deliveryStatus) => {
        const { occasion, onUpdateBookingDeliveryStatusFunc } = this.props;
        if (!onUpdateBookingDeliveryStatusFunc || !occasion?.id) return;
        onUpdateBookingDeliveryStatusFunc(occasion.id, deliveryStatus);
    };

    // Same gate as a legacy booking row: the control appears once gameplay has been
    // sent, which is also the point the occasion is pushed to AVAILABLE.
    renderDeliveryStatus() {
        const { occasion } = this.props;
        if (getGameplayState(occasion) === 'none') return null;

        return (
            <ACDeliveryStatusSelect
                value={occasion?.deliveryStatus}
                onChangeFunc={this.onChangeDeliveryStatus}
                usePortal={true}
            />
        );
    }

    // TEST AFFORDANCE: force the occasion to "ready for delivery" (AVAILABLE +
    // queue SENT) so the on-the-day features can be exercised, skipping every check
    // and sending no email. Hidden in production. Remove once delivery is testable
    // through the real send-gameplay flow.
    onClickMarkReady = (event) => {
        event.stopPropagation();
        const { occasion, onMarkBookingReadyFunc } = this.props;
        if (!onMarkBookingReadyFunc || !occasion?.id) return;

        const proceed = window.confirm(
            'TEST ONLY\n\nForce this occasion to "ready for delivery"?\n\n' +
            'This skips all checks, sends no email, and pushes the delivery status ' +
            'to the events API for the real HQ event.'
        );
        if (!proceed) return;

        onMarkBookingReadyFunc(occasion.id);
    };

    renderMarkReadyButton() {
        if (appConfig.NODE_ENV === 'production') return null;

        const { occasion } = this.props;
        const isReady = occasion?.deliveryStatus === 'AVAILABLE';

        return (
            <Tooltip
                title={
                    isReady
                        ? 'TEST ONLY: already marked ready for delivery — click to re-apply'
                        : 'TEST ONLY: force ready for delivery (skips all checks, sends no email)'
                }
            >
                <IconButton
                    style={{ border: `0.8px solid ${isReady ? 'rgb(255, 245, 157)' : 'rgb(224, 224, 224)'}` }}
                    className={styles.roundIconButton}
                    onClick={this.onClickMarkReady}
                >
                    <FlashOn style={{ color: isReady ? 'rgb(251, 192, 45)' : 'rgb(158, 158, 158)' }} />
                </IconButton>
            </Tooltip>
        );
    }

    renderNotificationButtons() {
        const { occasion } = this.props;
        const notification = occasion?.notification || {};

        const confirmationState = getConfirmationState(occasion);
        const confirmationText = getNotificationStateText(
            confirmationState,
            getLatestNotificationProperties(notification, 'confirmations'),
            { idleText: 'Send Confirmation' }
        );

        const gameplayState = getGameplayState(occasion);
        const canSendGameplay = this.hasDeliverableComponents();
        const gameplayText = getNotificationStateText(
            gameplayState,
            getLatestNotificationProperties(notification, 'codes', occasion),
            {
                idleText: canSendGameplay
                    ? 'Send Gameplay'
                    : 'Assign a route, a virtual or a ticket to a component before sending gameplay',
                sentPrefix: 'Gameplay ',
            }
        );

        return (
            <div style={{ display: "flex", gap: '8px', alignItems: 'center' }}>
                <Tooltip title={confirmationText}>
                    <IconButton
                        style={{ border: `0.8px solid ${stateBorderColors[confirmationState]}` }}
                        className={styles.roundIconButton}
                        onClick={this.onClickSendConfirmation}
                    >
                        <Email style={{ color: NOTIFICATION_STATE_COLORS[confirmationState] }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title={gameplayText}>
                    <span>
                        <IconButton
                            style={{ border: `0.8px solid ${stateBorderColors[gameplayState]}` }}
                            className={styles.roundIconButton}
                            onClick={this.onClickSendGameplay}
                            disabled={!canSendGameplay}
                        >
                            <SportsEsports style={{ color: NOTIFICATION_STATE_COLORS[gameplayState] }} />
                        </IconButton>
                    </span>
                </Tooltip>
                {this.renderMarkReadyButton()}
            </div>
        );
    }

    /** The currency the occasion's costs are quoted in. */
    currencySymbol() {
        const { occasion } = this.props;
        return getCurrencySymbolFromCurrencyShortcode(occasion?.currencyCode || 'AUD');
    }

    /**
     * The occasion's payout: the HQ-derived ratio, and the provider cost it is measured
     * against. ac-experience computes the ratio for the occasion because it is one HQ event —
     * but until now nothing drew it, because an occasion is rendered by this component and
     * never by `Booking`, which is where the traffic light lives.
     *
     * The colour bands are unchanged (green to 50% on AUD/NZD/INR). Delivery's KPI is a 40%
     * payout ratio; that is a target to read the number against, not a threshold here.
     */
    renderPayout() {
        const { occasion } = this.props;
        const cost = occasionCost(occasion);
        const showRatio = hasPayoutRatio(occasion);

        if (!showRatio && !cost) {
            return null;
        }

        const percentage = occasion?.payoutRatio || 0;
        const color = getPayoutTrafficColor(occasion?.currencyCode || 'AUD', percentage);

        return (
            <div className={styles.occasionPayout}>
                {showRatio && (
                    <Tooltip title={<span style={{ fontSize: '13px' }}>Payout ratio</span>} placement="top">
                        <span className={styles.occasionPayoutRatio}>
                            <span
                                className={styles.occasionPayoutDot}
                                style={{ backgroundColor: color || 'transparent' }}
                            />
                            {percentage.toFixed(2)}%
                        </span>
                    </Tooltip>
                )}
                <span className={styles.occasionPayoutCost}>
                    Cost {this.currencySymbol()}{getDecimalNumber(cost, 2)}
                </span>
            </div>
        );
    }

    /**
     * The occasion's own add-ons and upgrades: everything bought for it, tags already stripped by
     * aex (`booking/component-booking.ts` hqForOccasion).
     *
     * This is where anything the customer did not attribute to a single component belongs —
     * dietaries, extra-attendee counters, gift cards, and every occasion booked before the
     * checkout started tagging. Those used to be copied onto every component row instead, which
     * made one component's add-on look like it applied to all of them, and gave ops nowhere to
     * read the occasion's own extras.
     */
    renderExtras() {
        const { occasion } = this.props;
        const extras = occasion?.hq?.extras;
        if (isEmpty(extras) || (!extras.hasAddOns && !extras.hasUpgrade && !extras.addOns)) {
            return null;
        }

        return (
            <div className={styles.occasionExtras}>
                {extras.hasAddOns && (
                    <span className={styles.occasionExtrasBadge} style={{ color: 'red' }}>
                        <Check className={styles.occasionExtrasIcon} /> AddOns
                    </span>
                )}
                {extras.hasUpgrade && (
                    <span className={styles.occasionExtrasBadge} style={{ color: '#7b1fa2' }}>
                        <ArrowUpward className={styles.occasionExtrasIcon} /> Upgrade
                    </span>
                )}
                {extras.addOns && <span className={styles.occasionExtrasText}>{extras.addOns}</span>}
            </div>
        );
    }

    onSelectBooking(booking) {
        if (this.props.booking.id !== booking.id) {
            this.setState({
                booking,
            });
        }
    }

    getPackageOwners() {
        const { packageOwners = [], occasion } = this.props;
        let userId = -1;
        let selectedUser = {};

        if (occasion.userId) {
            userId = occasion.userId;
        }

        if (packageOwners && !isEmpty(packageOwners)) {
            selectedUser = packageOwners.find((u) => u.id === userId);
        }

        if (isEmpty(selectedUser)) {
            selectedUser = {
                label: '',
                value: -1,
            };
        } else {
            selectedUser = {
                label: selectedUser.name,
                value: selectedUser.id,
            };
        }

        return selectedUser;
    }


    render() {
        const { occasion, packageOwners } = this.props;

        const { booking } = this.props;
        const { dateText, timeText } = getBookingEventDate(occasion);
        this.scrollToRef(this.bookingReference);


        let selectedPackageOwner = this.getPackageOwners();

        let mUsers = [];

        if (!packageOwners) return null;

        packageOwners.forEach((user, index) => {
            let mUser = {
                label: user.name,
                value: user.id,
            };
            mUsers.push(mUser);
        });

        return (
            <div className={styles.root}>
                <Box className={styles.occasionWrapper}>
                    <div className={styles.occasionHeader}>
                        {/*
                          * The HQ event reference and the event date, which every component row
                          * used to repeat: a component inherits the occasion's `hq` untouched, so
                          * N rows printed the same link and the same date. This was the Mongo
                          * `_id` — underlined and pointer-cursored, but not a link and not a
                          * reference anyone can quote.
                          */}
                        <div className={styles.occasionHeaderLeft}>
                            <a
                                className={styles.occasionReference}
                                href={getHqEventUrl(occasion)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {getHqEventReference(occasion)}
                            </a>
                            <span className={styles.occasionDate}>
                                {dateText}{timeText}
                            </span>
                        </div>
                        <div className={styles.occasionHeaderRight}>
                            <div className={styles.occasionCustomerInfo}>
                                <p className={styles.customerName}>{occasion?.customer?.name}</p>
                                <p className={styles.customerPhone}>{occasion?.customer?.phone}</p>
                            </div>
                            <div className={styles.selectContainer}>
                                <div className={styles.selectInputContainer}>
                                    <span className={styles.packageOwnerText}>Package owner</span>
                                    <Select
                                        className={styles.usersSelect}
                                        TextFieldProps={{
                                            label: 'Provider Name 2',
                                            InputLabelProps: {
                                                shrink: true,
                                            },
                                            placeholder: 'Select a user',
                                        }}
                                        options={mUsers}
                                        value={selectedPackageOwner}
                                        onChange={this.onChangeBookingManager}
                                        style={dropdownStyles}
                                        menuPortalTarget={document.body}
                                    />
                                </div>
                            </div>
                            {this.renderPayout()}
                            {/* The occasion's own hq is the one HQ keeps up to date. */}
                            <ACBookingStatusPill hq={occasion?.hq || {}} />
                            {this.renderDeliveryStatus()}
                            {this.renderNotificationButtons()}
                            <div className={styles.wCtaButton}>
                                W
                            </div>
                        </div>
                    </div>
                    {this.renderExtras()}
                    <div className={styles.occasionBody}>
                        {this.renderBookings(occasion?.bookings, booking)}
                    </div>
                </Box>
            </div>
        )
    }
}